import React, { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import {
  Briefcase,
  GraduationCap,
  Code,
  Award,
  Github,
  Plus,
  Trash2,
  Save,
  X,
  Edit,
} from "lucide-react";
import Navbar from "../components/common/Navbar";
import Modal from "../components/common/Modal";
import { mockUserData, type UserProfile } from "../data/mockUserData";
import { formatDate } from "../utils/profileUtils";
import toast from "react-hot-toast";

/**
 * ProfileEditPage Component
 * Allows users to edit their profile information including experience, education, skills, etc.
 * Features: Modal-based editing for different sections, form validation, and real-time updates
 */
const ProfileEditPage: React.FC = () => {
  const navigate = useNavigate();
  const { username } = useParams<{ username: string }>();
  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [showEducationModal, setShowEducationModal] = useState(false);
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [showCertificationModal, setShowCertificationModal] = useState(false);
  const [showOpenSourceModal, setShowOpenSourceModal] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  // Mock user data - in production, this would come from an API
  const userData: UserProfile = mockUserData;

  // Experience Form
  const experienceForm = useForm({
    defaultValues: {
      title: "",
      company: "",
      companyLogo: "",
      location: "",
      startDate: "",
      endDate: "",
      description: "",
      achievements: [""],
    },
  });

  // Education Form
  const educationForm = useForm({
    defaultValues: {
      degree: "",
      school: "",
      schoolLogo: "",
      field: "",
      startDate: "",
      endDate: "",
      gpa: "",
      description: "",
    },
  });

  // Skill Form
  const skillForm = useForm({
    defaultValues: {
      name: "",
      level: "초급" as const,
      category: "Frontend" as const,
    },
  });

  // Certification Form
  const certificationForm = useForm({
    defaultValues: {
      name: "",
      issuer: "",
      issueDate: "",
      expiryDate: "",
      credentialId: "",
      verificationUrl: "",
    },
  });

  // Open Source Form
  const openSourceForm = useForm({
    defaultValues: {
      projectName: "",
      description: "",
      repository: "",
      contributions: 0,
      stars: 0,
      language: "",
      lastContribution: "",
      role: "Contributor" as const,
    },
  });

  // Handle form submissions
  const handleExperienceSubmit = (data: any) => {
    console.log("Experience data:", data);
    toast.success("경력이 추가되었습니다!");
    setShowExperienceModal(false);
    experienceForm.reset();
  };

  const handleEducationSubmit = (data: any) => {
    console.log("Education data:", data);
    toast.success("학력이 추가되었습니다!");
    setShowEducationModal(false);
    educationForm.reset();
  };

  const handleSkillSubmit = (data: any) => {
    console.log("Skill data:", data);
    toast.success("기술이 추가되었습니다!");
    setShowSkillModal(false);
    skillForm.reset();
  };

  const handleCertificationSubmit = (data: any) => {
    console.log("Certification data:", data);
    toast.success("자격증이 추가되었습니다!");
    setShowCertificationModal(false);
    certificationForm.reset();
  };

  const handleOpenSourceSubmit = (data: any) => {
    console.log("Open Source data:", data);
    toast.success("오픈소스 기여가 추가되었습니다!");
    setShowOpenSourceModal(false);
    openSourceForm.reset();
  };

  // Edit handlers
  const handleEditExperience = (item: any) => {
    setEditingItem(item);
    experienceForm.reset(item);
    setShowExperienceModal(true);
  };

  const handleEditEducation = (item: any) => {
    setEditingItem(item);
    educationForm.reset(item);
    setShowEducationModal(true);
  };


  const handleEditCertification = (item: any) => {
    setEditingItem(item);
    certificationForm.reset(item);
    setShowCertificationModal(true);
  };

  const handleEditOpenSource = (item: any) => {
    setEditingItem(item);
    openSourceForm.reset(item);
    setShowOpenSourceModal(true);
  };

  // Delete handlers
  const handleDeleteExperience = (id: number) => {
    console.log("Delete experience:", id);
    toast.success("경력이 삭제되었습니다!");
  };

  const handleDeleteEducation = (id: number) => {
    console.log("Delete education:", id);
    toast.success("학력이 삭제되었습니다!");
  };

  const handleDeleteSkill = (index: number) => {
    console.log("Delete skill:", index);
    toast.success("기술이 삭제되었습니다!");
  };

  const handleDeleteCertification = (id: number) => {
    console.log("Delete certification:", id);
    toast.success("자격증이 삭제되었습니다!");
  };

  const handleDeleteOpenSource = (id: number) => {
    console.log("Delete open source:", id);
    toast.success("오픈소스 기여가 삭제되었습니다!");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              프로필 수정
            </h1>
            <p className="text-gray-600">
              프로필 정보를 수정하고 새로운 경험을 추가하세요
            </p>
          </div>

          {/* Profile Sections */}
          <div className="space-y-6">
            {/* Experience Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Briefcase className="w-5 h-5" />
                  경력
                </h2>
                <button
                  onClick={() => setShowExperienceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  경력 추가
                </button>
              </div>
              <div className="space-y-4">
                {userData.experience.map((exp) => (
                  <div
                    key={exp.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {exp.title}
                        </h3>
                        <p className="text-gray-600 font-medium">
                          {exp.company}
                        </p>
                        <p className="text-sm text-gray-500 mb-2">
                          {formatDate(exp.startDate)} -{" "}
                          {exp.endDate ? formatDate(exp.endDate) : "현재"}
                        </p>
                        <p className="text-gray-700 text-sm">
                          {exp.description}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditExperience(exp)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteExperience(exp.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Education Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <GraduationCap className="w-5 h-5" />
                  학력
                </h2>
                <button
                  onClick={() => setShowEducationModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  학력 추가
                </button>
              </div>
              <div className="space-y-4">
                {userData.education.map((edu) => (
                  <div
                    key={edu.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {edu.degree}
                        </h3>
                        <p className="text-gray-600 font-medium">
                          {edu.school} - {edu.field}
                        </p>
                        <p className="text-sm text-gray-500 mb-2">
                          {formatDate(edu.startDate)} -{" "}
                          {formatDate(edu.endDate)}
                        </p>
                        <p className="text-sm text-gray-600">학점: {edu.gpa}</p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditEducation(edu)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteEducation(edu.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Skills Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  기술 스택
                </h2>
                <button
                  onClick={() => setShowSkillModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  기술 추가
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {userData.skills.map((skill, index) => (
                  <div
                    key={index}
                    className="flex items-center gap-2 px-3 py-1 bg-gray-100 rounded-full"
                  >
                    <span className="text-sm font-medium text-gray-700">
                      {skill.name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({skill.level})
                    </span>
                    <button
                      onClick={() => handleDeleteSkill(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {/* Certifications Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Award className="w-5 h-5" />
                  자격증
                </h2>
                <button
                  onClick={() => setShowCertificationModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  자격증 추가
                </button>
              </div>
              <div className="space-y-4">
                {userData.certifications.map((cert) => (
                  <div
                    key={cert.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {cert.name}
                        </h3>
                        <p className="text-gray-600 font-medium">
                          {cert.issuer}
                        </p>
                        <p className="text-sm text-gray-500">
                          발급일: {formatDate(cert.issueDate)}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditCertification(cert)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCertification(cert.id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Open Source Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                  <Github className="w-5 h-5" />
                  오픈소스 기여
                </h2>
                <button
                  onClick={() => setShowOpenSourceModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  기여 추가
                </button>
              </div>
              <div className="space-y-4">
                {userData.openSourceContributions.map((contribution) => (
                  <div
                    key={contribution.id}
                    className="border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {contribution.projectName}
                        </h3>
                        <p className="text-gray-600 mb-2">
                          {contribution.description}
                        </p>
                        <p className="text-sm text-gray-500">
                          Repository: {contribution.repository}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEditOpenSource(contribution)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteOpenSource(contribution.id)
                          }
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-4 mt-8">
            <button
              onClick={() => navigate(`/user/${username}`)}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              onClick={() => {
                toast.success("프로필이 저장되었습니다!");
                navigate(`/user/${username}`);
              }}
              className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Save className="w-4 h-4" />
              저장
            </button>
          </div>
        </div>
      </div>

      {/* Experience Modal */}
      <Modal
        isOpen={showExperienceModal}
        onClose={() => {
          setShowExperienceModal(false);
          setEditingItem(null);
          experienceForm.reset();
        }}
        title={editingItem ? "경력 수정" : "경력 추가"}
      >
        <form
          onSubmit={experienceForm.handleSubmit(handleExperienceSubmit)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              직책 <span className="text-red-500">*</span>
            </label>
            <input
              {...experienceForm.register("title", {
                required: "직책을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: Senior Frontend Developer"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              회사 <span className="text-red-500">*</span>
            </label>
            <input
              {...experienceForm.register("company", {
                required: "회사를 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: 테커 테크"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="month"
                {...experienceForm.register("startDate", {
                  required: "시작일을 입력해주세요",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료일
              </label>
              <input
                type="month"
                {...experienceForm.register("endDate")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <textarea
              {...experienceForm.register("description")}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="업무 내용을 설명해주세요"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowExperienceModal(false);
                setEditingItem(null);
                experienceForm.reset();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingItem ? "수정" : "추가"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Education Modal */}
      <Modal
        isOpen={showEducationModal}
        onClose={() => {
          setShowEducationModal(false);
          setEditingItem(null);
          educationForm.reset();
        }}
        title={editingItem ? "학력 수정" : "학력 추가"}
      >
        <form
          onSubmit={educationForm.handleSubmit(handleEducationSubmit)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              학위 <span className="text-red-500">*</span>
            </label>
            <input
              {...educationForm.register("degree", {
                required: "학위를 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: 컴퓨터공학 학사"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              학교 <span className="text-red-500">*</span>
            </label>
            <input
              {...educationForm.register("school", {
                required: "학교를 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: 서울대학교"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              전공 <span className="text-red-500">*</span>
            </label>
            <input
              {...educationForm.register("field", {
                required: "전공을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: 컴퓨터공학과"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="month"
                {...educationForm.register("startDate", {
                  required: "시작일을 입력해주세요",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                종료일
              </label>
              <input
                type="month"
                {...educationForm.register("endDate")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              학점
            </label>
            <input
              {...educationForm.register("gpa")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: 3.8/4.0"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowEducationModal(false);
                setEditingItem(null);
                educationForm.reset();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingItem ? "수정" : "추가"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Skill Modal */}
      <Modal
        isOpen={showSkillModal}
        onClose={() => {
          setShowSkillModal(false);
          setEditingItem(null);
          skillForm.reset();
        }}
        title={editingItem ? "기술 수정" : "기술 추가"}
      >
        <form
          onSubmit={skillForm.handleSubmit(handleSkillSubmit)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              기술명 <span className="text-red-500">*</span>
            </label>
            <input
              {...skillForm.register("name", {
                required: "기술명을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: React"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                숙련도 <span className="text-red-500">*</span>
              </label>
              <select
                {...skillForm.register("level", {
                  required: "숙련도를 선택해주세요",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="초급">초급</option>
                <option value="중급">중급</option>
                <option value="고급">고급</option>
                <option value="전문가">전문가</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                카테고리 <span className="text-red-500">*</span>
              </label>
              <select
                {...skillForm.register("category", {
                  required: "카테고리를 선택해주세요",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Frontend">Frontend</option>
                <option value="Backend">Backend</option>
                <option value="Cloud">Cloud</option>
                <option value="DevOps">DevOps</option>
                <option value="Tools">Tools</option>
                <option value="Design">Design</option>
              </select>
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowSkillModal(false);
                setEditingItem(null);
                skillForm.reset();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingItem ? "수정" : "추가"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Certification Modal */}
      <Modal
        isOpen={showCertificationModal}
        onClose={() => {
          setShowCertificationModal(false);
          setEditingItem(null);
          certificationForm.reset();
        }}
        title={editingItem ? "자격증 수정" : "자격증 추가"}
      >
        <form
          onSubmit={certificationForm.handleSubmit(handleCertificationSubmit)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              자격증명 <span className="text-red-500">*</span>
            </label>
            <input
              {...certificationForm.register("name", {
                required: "자격증명을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: AWS Certified Solutions Architect"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              발급기관 <span className="text-red-500">*</span>
            </label>
            <input
              {...certificationForm.register("issuer", {
                required: "발급기관을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: Amazon Web Services"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                발급일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                {...certificationForm.register("issueDate", {
                  required: "발급일을 입력해주세요",
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                만료일
              </label>
              <input
                type="date"
                {...certificationForm.register("expiryDate")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              자격증 ID
            </label>
            <input
              {...certificationForm.register("credentialId")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: AWS-123456789"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              검증 URL
            </label>
            <input
              {...certificationForm.register("verificationUrl")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: https://aws.amazon.com/verification"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowCertificationModal(false);
                setEditingItem(null);
                certificationForm.reset();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingItem ? "수정" : "추가"}
            </button>
          </div>
        </form>
      </Modal>

      {/* Open Source Modal */}
      <Modal
        isOpen={showOpenSourceModal}
        onClose={() => {
          setShowOpenSourceModal(false);
          setEditingItem(null);
          openSourceForm.reset();
        }}
        title={editingItem ? "오픈소스 기여 수정" : "오픈소스 기여 추가"}
      >
        <form
          onSubmit={openSourceForm.handleSubmit(handleOpenSourceSubmit)}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              프로젝트명 <span className="text-red-500">*</span>
            </label>
            <input
              {...openSourceForm.register("projectName", {
                required: "프로젝트명을 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: React"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              설명
            </label>
            <textarea
              {...openSourceForm.register("description")}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="기여 내용을 설명해주세요"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Repository <span className="text-red-500">*</span>
            </label>
            <input
              {...openSourceForm.register("repository", {
                required: "Repository를 입력해주세요",
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="예: facebook/react"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                기여 수
              </label>
              <input
                type="number"
                {...openSourceForm.register("contributions", {
                  valueAsNumber: true,
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 15"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                스타 수
              </label>
              <input
                type="number"
                {...openSourceForm.register("stars", { valueAsNumber: true })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 220000"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                언어
              </label>
              <input
                {...openSourceForm.register("language")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: JavaScript"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                역할
              </label>
              <select
                {...openSourceForm.register("role")}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="Contributor">Contributor</option>
                <option value="Maintainer">Maintainer</option>
                <option value="Owner">Owner</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              마지막 기여일
            </label>
            <input
              type="date"
              {...openSourceForm.register("lastContribution")}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => {
                setShowOpenSourceModal(false);
                setEditingItem(null);
                openSourceForm.reset();
              }}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              취소
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              {editingItem ? "수정" : "추가"}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default ProfileEditPage;
